import React, { useState, useEffect } from "react";
import {
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  IconButton,
  Box,
  Menu,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";



interface AnnotationManagerProps {
  annotations: PlotlyAnnotation[];
  shapes: PlotlyShape[];
  onUpdate: (annotations: PlotlyAnnotation[], shapes: PlotlyShape[]) => void;
}

let idCounter = 0;

const AnnotationManager: React.FC<AnnotationManagerProps> = ({
  annotations: plotlyAnnotations,
  shapes: plotlyShapes,
  onUpdate,
}) => {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [newType, setNewType] = useState<AnnotationType>("hline");

  // Initialize from props
  useEffect(() => {
    const initialAnnotations: Annotation[] = [
      ...plotlyAnnotations.map((ann): TextAnnotation => ({
        id: ++idCounter,
        type: ann.type === "text-paper" || ann.type === "text-plot" || ann.type === "text-caption" ? ann.type : 
              ann.xref === "paper" || ann.yref === "paper" ? "text-paper" : "text-plot",
        x: ann.x ?? 0,
        y: ann.y ?? 0,
        text: ann.text ?? "",
        fontSize: ann.font?.size,
        color: ann.font?.color,
      })),
      ...plotlyShapes.map((shape): LineAnnotation => {
        const isHLine = shape.y0 === shape.y1;
        return {
          id: ++idCounter,
          type: isHLine ? "hline" : "vline",
          value: isHLine ? shape.y0 ?? 0 : shape.x0 ?? 0,
          width: shape.line?.width,
          color: shape.line?.color,
          dash: shape.line?.dash ?? "solid",
        };
      }),
    ];
    setAnnotations(initialAnnotations);
  }, [plotlyAnnotations, plotlyShapes]);

  const handleChange = (id: number, key: string, value: any) => {
    // Update only local state
    setAnnotations((prev) =>
      prev.map((a) => (a.id === id ? { ...a, [key]: value } : a))
    );
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      const plotlyAnnotations: PlotlyAnnotation[] = annotations
        .filter((a): a is TextAnnotation => a.type === "text-plot" || a.type === "text-paper" || a.type == "text-caption")
        .map((a) => ({
          x: a.x,
          y: a.y,
          text: a.text,
          showarrow: false,
          xref: a.type === "text-plot" ? "x" : "paper",
          yref: a.type === "text-plot" ? "y" : "paper",
          font: {
            size: a.fontSize,
            color: a.color,
          },
          type: a.type,
        }));

      const plotlyShapes: PlotlyShape[] = annotations
        .filter((a): a is LineAnnotation => a.type === "hline" || a.type === "vline")
        .map((a) =>
          a.type === "hline"
            ? {
                type: "line",
                x0: 0,
                x1: 1,
                y0: a.value,
                y1: a.value,
                line: {
                  width: a.width,
                  color: a.color,
                  dash: a.dash ?? "solid"
                },
                xref: "paper",
                yref: "y",
              }
            : {
                type: "line",
                x0: a.value,
                x1: a.value,
                y0: 0,
                y1: 1,
                line: {
                  width: a.width,
                  color: a.color,
                  dash: a.dash ?? "solid"
                },
                xref: "x",
                yref: "paper",
              }
        );
      // console.log("UseEffect: Updating plotly annotations:", plotlyAnnotations, "and shapes:", plotlyShapes);
      onUpdate(plotlyAnnotations, plotlyShapes);
    }, 300); // Debounce slightly

    return () => clearTimeout(timeout);
  }, [annotations]);


  const handleAdd = (type?: AnnotationType) => {
    const id = ++idCounter;
    const annotationType = type ?? newType; // fallback to state if not passed
    let newAnnotation: Annotation;

    if (annotationType === "text-plot" || annotationType === "text-paper" || annotationType == "text-caption") {
      newAnnotation = {
        id,
        type: annotationType,
        x: 0.5,
        y: 0.5,
        text: "New Text",
      };
    } else {
      newAnnotation = {
        id,
        type: annotationType,
        value: 0,
      };
    }

    setAnnotations([...annotations, newAnnotation]);
  };

  const handleDelete = (id: number) => {
    setAnnotations(annotations.filter((a) => a.id !== id));
  };

  const AddAnnotationDropdown = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleSelectType = (type: AnnotationType) => {
      handleAdd(type); // Call the add handler with the selected type
      setNewType(type);
      handleClose();
    };

    return (
      <>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleClick}
          color="primary"
        >
          Add Annotation
        </Button>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={() => handleSelectType("hline")}>
            Horizontal Line
          </MenuItem>
          <MenuItem onClick={() => handleSelectType("vline")}>
            Vertical Line
          </MenuItem>
          <MenuItem onClick={() => handleSelectType("text-plot")}>
            Text (Plot)
          </MenuItem>
          <MenuItem onClick={() => handleSelectType("text-paper")}>
            Text (Paper)
          </MenuItem>
          <MenuItem onClick={() => handleSelectType("text-caption")}>
            Plot Caption
          </MenuItem>
        </Menu>
      </>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-start" sx={{ ml: 2 }}>
        <AddAnnotationDropdown />
      </Box>
      {annotations.map((a) => (
        <Box
          key={a.id}
          sx={{ mt: 2, p: 2, borderRadius: 2, border: "1px solid", borderColor: "#ccc" }}
        >
          <Grid container spacing={2} alignItems="center" wrap="nowrap">
            {/* Annotation type label */}
            <Grid item>
              <Box sx={{ fontWeight: 500, width: "100px", textTransform: "capitalize" }}>
                {(() => {
                  switch (a.type) {
                    case "hline":
                      return "Horiz. Line";
                    case "vline":
                      return "Vert. Line";
                    case "text-plot":
                      return "Text (Plot)";
                    case "text-paper":
                      return "Text (Paper)";
                    case "text-caption": 
                      return "Plot Caption";
                  }
                })()}
              </Box>
            </Grid>

            {/* Form fields fill remaining space */}
            <Grid item sx={{ flexGrow: 1, minWidth: 0 }}>
              {a.type === "text-plot" || a.type === "text-paper" || a.type === "text-caption" ? (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap" }}>
                  <TextField
                    label="Text"
                    value={a.text}
                    onChange={(e) => handleChange(a.id, "text", e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label={a.type === "text-paper" || a.type === "text-caption" ? "X Paper" : "X Plot"}
                    type="number"
                    value={a.x}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleChange(a.id, "x", value === "" || isNaN(parseFloat(value)) ? "" : parseFloat(value));
                    }}
                    sx={{ maxWidth: 100 }}
                  />
                  <TextField
                    label={a.type === "text-paper" || a.type === "text-caption" ? "Y Paper" : "Y Plot"}
                    type="number"
                    value={a.y}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleChange(a.id, "y", value === "" || isNaN(parseFloat(value)) ? "" : parseFloat(value));
                    }}
                    sx={{ maxWidth: 100 }}
                  />
                  <TextField
                    label="Font Size"
                    type="number"
                    value={a.fontSize ?? ""}
                    onChange={(e) => handleChange(a.id, "fontSize", parseFloat(e.target.value))}
                    sx={{ maxWidth: 100 }}
                  />
                  <TextField
                    label="Color"
                    value={a.color ?? ""}
                    onChange={(e) => handleChange(a.id, "color", e.target.value)}
                    sx={{ maxWidth: 100 }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label={a.type === "hline" ? "Y Plot" : "X Plot"}
                    type="number"
                    value={(a as LineAnnotation).value}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleChange(a.id, "value", value === "" || isNaN(parseFloat(value)) ? "" : parseFloat(value));
                    }}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="Line Width"
                    type="number"
                    value={(a as LineAnnotation).width ?? ""}
                    onChange={(e) => handleChange(a.id, "width", parseFloat(e.target.value))}
                    sx={{ maxWidth: 150 }}
                  />
                  <FormControl sx={{width: 200}}>
                    <InputLabel>Dash</InputLabel>
                    <Select
                      label="Dash"
                      value={(a as LineAnnotation).dash ?? "solid"}
                      onChange={(e) => handleChange(a.id, "dash", e.target.value)}
                    >
                      <MenuItem value="solid">Solid</MenuItem>
                      <MenuItem value="dash">Dashed</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Color"
                    value={a.color ?? ""}
                    onChange={(e) => handleChange(a.id, "color", e.target.value)}
                    sx={{ maxWidth: 100 }}
                  />
                </Box>
              )}
            </Grid>

            {/* Delete button pinned to right */}
            <Grid item>
              <IconButton onClick={() => handleDelete(a.id)} size="small">
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );

};

export default AnnotationManager;